import React, { createContext, useState, useCallback } from 'react';
import { FiCheckCircle, FiXCircle, FiAlertCircle, FiInfo, FiX } from 'react-icons/fi';

export const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success', duration = 3000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };
    
    setToasts(prev => [...prev, newToast]);

    setTimeout(() => {
      removeToast(id);
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const toast = {
    success: (msg, duration) => addToast(msg, 'success', duration),
    error: (msg, duration) => addToast(msg, 'error', duration),
    warning: (msg, duration) => addToast(msg, 'warning', duration),
    info: (msg, duration) => addToast(msg, 'info', duration),
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <FiCheckCircle />;
      case 'error': return <FiXCircle />;
      case 'warning': return <FiAlertCircle />;
      case 'info': return <FiInfo />;
      default: return <FiInfo />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success': return '#10B981';
      case 'error': return '#EF4444';
      case 'warning': return '#F59E0B';
      case 'info': return '#3B82F6';
      default: return '#3B82F6';
    }
  };

  return (
    <ToastContext.Provider value={{ toast, removeToast }}>
      {children}
      
      <style>{`
        .toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          display: flex;
          flex-direction: column;
          gap: 12px;
          font-family: 'Outfit', sans-serif;
        }
        
        .toast-item {
          min-width: 320px;
          max-width: 420px;
          background: #FFFFFF;
          border-radius: 12px;
          padding: 16px 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          gap: 12px;
          animation: slideIn 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          border-left: 4px solid;
          position: relative;
          overflow: hidden;
        }
        
        .toast-item::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          height: 3px;
          background: currentColor;
          animation: progress 3s linear forwards;
        }
        
        .toast-item.toast-success { border-left-color: #10B981; }
        .toast-item.toast-error { border-left-color: #EF4444; }
        .toast-item.toast-warning { border-left-color: #F59E0B; }
        .toast-item.toast-info { border-left-color: #3B82F6; }
        
        .toast-icon {
          font-size: 24px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
        }
        
        .toast-success .toast-icon { color: #10B981; }
        .toast-error .toast-icon { color: #EF4444; }
        .toast-warning .toast-icon { color: #F59E0B; }
        .toast-info .toast-icon { color: #3B82F6; }
        
        .toast-content {
          flex: 1;
        }
        
        .toast-message {
          font-size: 14px;
          font-weight: 600;
          color: #0F172A;
          line-height: 1.5;
          margin: 0;
        }
        
        .toast-close {
          background: none;
          border: none;
          color: #94A3B8;
          cursor: pointer;
          font-size: 20px;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          flex-shrink: 0;
          width: 24px;
          height: 24px;
          border-radius: 6px;
        }
        
        .toast-close:hover {
          background: #F1F5F9;
          color: #0F172A;
          transform: rotate(90deg);
        }
        
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes progress {
          from { width: 100%; }
          to { width: 0%; }
        }
        
        @media (max-width: 768px) {
          .toast-container {
            right: 10px;
            left: 10px;
            top: 10px;
          }
          .toast-item {
            min-width: auto;
            max-width: 100%;
          }
        }
      `}</style>
      
      <div className="toast-container">
        {toasts.map((toast) => (
          <div 
            key={toast.id} 
            className={`toast-item toast-${toast.type}`}
            style={{ '--toast-color': getTypeColor(toast.type) }}
          >
            <div className="toast-icon">{getIcon(toast.type)}</div>
            <div className="toast-content">
              <p className="toast-message">{toast.message}</p>
            </div>
            <button 
              className="toast-close" 
              onClick={() => removeToast(toast.id)}
              aria-label="Close"
            >
              <FiX />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};